/**
 * Land on the thing a report is about.
 *
 * A reviewer reading a report clicks "open the page" and would otherwise
 * arrive at the top of a dashboard with no idea which of nine panels the
 * reporter meant. The admin portal appends `?feedback_focus=<selector>` to the
 * link; this finds that element, scrolls to it and outlines it for a moment.
 *
 * The selector is the short structural one stored on the report. It arrives
 * through a URL, so it is treated as untrusted input: it is only ever handed
 * to `querySelector` (which cannot execute anything), the lookup is wrapped
 * because an invalid selector throws, and nothing from it is ever written into
 * the DOM as markup.
 */

export const FOCUS_PARAM = 'feedback_focus';
const HIGHLIGHT_MS = 4000;
const RETRY_MS = 400;
const MAX_ATTEMPTS = 10;

/** Read the focus selector out of a hash-routed URL's query string. */
export function focusSelectorFrom(href) {
  if (!href) return null;
  const query = String(href).split('?').slice(1).join('?');
  if (!query) return null;
  const value = new URLSearchParams(query).get(FOCUS_PARAM);
  return value ? value.slice(0, 300) : null;
}

function outline(element) {
  const previous = {
    outline: element.style.outline,
    offset: element.style.outlineOffset,
    radius: element.style.borderRadius,
  };
  element.style.outline = '3px solid rgb(var(--color-state-focus, 59 130 246))';
  element.style.outlineOffset = '2px';
  element.scrollIntoView({ block: 'center', behavior: 'smooth' });
  window.setTimeout(() => {
    element.style.outline = previous.outline;
    element.style.outlineOffset = previous.offset;
    element.style.borderRadius = previous.radius;
  }, HIGHLIGHT_MS);
}

/**
 * Look for the element, retrying briefly: the page it belongs to almost
 * always fetches its data after mount, so the first look is too early.
 */
export function focusReportedElement(selector, { attempt = 0 } = {}) {
  if (!selector || typeof document === 'undefined') return false;
  let element = null;
  try {
    element = document.querySelector(selector);
  } catch {
    // A selector that no longer parses is not worth reporting to the reviewer;
    // they still have the page itself.
    return false;
  }
  if (element) {
    outline(element);
    return true;
  }
  if (attempt < MAX_ATTEMPTS) {
    window.setTimeout(() => focusReportedElement(selector, { attempt: attempt + 1 }), RETRY_MS);
  }
  return false;
}

/** Called once on mount by the feedback launcher. */
export function bindFeedbackFocus() {
  if (typeof window === 'undefined') return;
  const selector = focusSelectorFrom(window.location.href);
  if (selector) focusReportedElement(selector);
}
